/* Pulse "The technology" pinned-shoe scroll reveal.
 * Vanilla port of storefront/app/components/pulse/TechPin.tsx (Andre,
 * 2026-08-17). Two effects, both from the source, nothing added:
 *
 *   1. Publish the pinned head's height as --techpin-head-h on the section, so
 *      the shoe's sticky offset can sit directly beneath it. ResizeObserver.
 *   2. CONTINUOUS scroll progress, not a stepped index: as one step scrolls
 *      off its copy fades out while the next fades in, the shoe crossfades to
 *      the next colourway, and the single ring travels to the next part.
 *      p runs 0..n-1 across the steps, measured against the reference line
 *      (viewport centre on desktop; centre of the strip below the pinned shoe
 *      on mobile). The exit fade dissolves the pinned heading TOGETHER with
 *      the last step so it never stacks over the next section's heading.
 *
 * Ring geometry comes from data-x / data-y / data-d on each .techpin__step,
 * which sections/pulse-tech-pin.liquid writes from the block settings.
 *
 * There is no prefers-reduced-motion branch here because the source has none;
 * adding one would be new behaviour, not a port.
 */
(function () {
  if (window.__pulse_tech_pin) return;
  window.__pulse_tech_pin = true;

  var MOBILE = '(max-width: 899px)';

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function init(root) {
    if (!root || root.__pulseTechPin) return;
    root.__pulseTechPin = true;

    var head = root.querySelector('.techpin__head');
    var stage = root.querySelector('.techpin__stage');
    var ring = root.querySelector('.techpin__ring');
    var steps = toArray(root.querySelectorAll('.techpin__step'));
    var imgs = toArray(root.querySelectorAll('.techpin__img'));
    if (!steps.length) return;

    // Ring centre and diameter per step, in % of the shoe box.
    var pts = steps.map(function (el) {
      return {
        x: parseFloat(el.getAttribute('data-x')),
        y: parseFloat(el.getAttribute('data-y')),
        d: parseFloat(el.getAttribute('data-d'))
      };
    });
    var hasRing =
      !!ring &&
      pts.length > 1 &&
      pts.every(function (p) {
        return isFinite(p.x) && isFinite(p.y) && isFinite(p.d);
      });

    // Preload the later colourways so the crossfade never flashes.
    imgs.slice(1).forEach(function (el) {
      var img = new Image();
      img.src = el.currentSrc || el.src;
    });

    // The headline is pinned; the shoe must sit below it.
    var setHead = function () {
      if (head) root.style.setProperty('--techpin-head-h', head.offsetHeight + 'px');
    };
    var ro = null;
    if (head) {
      setHead();
      if (window.ResizeObserver) {
        ro = new ResizeObserver(setHead);
        ro.observe(head);
      } else {
        window.addEventListener('resize', setHead);
      }
    }

    var raf = 0;

    function paint() {
      raf = 0;

      var mobile = window.matchMedia(MOBILE).matches;
      var stageBottom =
        mobile && stage ? Math.max(0, stage.getBoundingClientRect().bottom) : 0;
      var mid = (stageBottom + window.innerHeight) / 2;

      var centres = steps.map(function (el) {
        var r = el.getBoundingClientRect();
        return r.top + r.height / 2;
      });

      // Piecewise-linear position of the reference line between step centres.
      var p = 0;
      var i;
      if (mid <= centres[0]) {
        p = 0;
      } else if (mid >= centres[centres.length - 1]) {
        p = centres.length - 1;
      } else {
        for (i = 0; i < centres.length - 1; i++) {
          if (mid >= centres[i] && mid <= centres[i + 1]) {
            p = i + (mid - centres[i]) / (centres[i + 1] - centres[i]);
            break;
          }
        }
      }

      // Copy: 1 at its own centre, down to 0.2 one full step away.
      steps.forEach(function (el, idx) {
        el.style.opacity = String(1 - Math.min(1, Math.abs(p - idx)) * 0.8);
      });

      // Exit: once the last step's centre rises past the reference line, both
      // it and the pinned head fade to 0 by the time that centre reaches the
      // bottom of the head.
      var lastEl = steps[steps.length - 1];
      if (head && lastEl) {
        var headBottom = head.getBoundingClientRect().bottom;
        var c = centres[centres.length - 1];
        var span = Math.max(1, mid - headBottom);
        var exit =
          c >= mid ? 1 : Math.max(0, Math.min(1, (c - headBottom) / span));
        /* Phones fade the head's CHILDREN through --techpin-exit (see the style
           block in sections/pulse-tech-pin.liquid, 2026-09-10): fading the whole
           head made its surface transparent and the steps passing behind it
           showed through. Desktop keeps the original whole-head fade. */
        if (mobile) {
          head.style.opacity = '';
          head.style.setProperty('--techpin-exit', String(exit));
        } else {
          head.style.removeProperty('--techpin-exit');
          head.style.opacity = String(exit);
        }
        lastEl.style.opacity = String(Number(lastEl.style.opacity) * exit);
      }

      // Shoe: neighbouring colourways crossfade through the transition.
      imgs.forEach(function (img, idx) {
        img.style.opacity = String(Math.max(0, 1 - Math.abs(p - idx)));
      });

      // Ring: interpolate centre and size between the two nearest parts.
      if (hasRing) {
        var i0 = Math.min(pts.length - 2, Math.floor(p));
        var t = p - i0;
        var A = pts[i0];
        var B = pts[i0 + 1];
        // Composited motion keeps the same hotspot geometry without layout shifts.
        var shoeBox = ring.parentElement;
        var x = (A.x + (B.x - A.x) * t) * shoeBox.clientWidth / 100;
        var y = (A.y + (B.y - A.y) * t) * shoeBox.clientHeight / 100;
        ring.style.left = '0';
        ring.style.top = '0';
        ring.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) translate(-50%,-50%)';
        ring.style.width = A.d + (B.d - A.d) * t + '%';
      }
    }

    function onScroll() {
      if (!raf) raf = requestAnimationFrame(paint);
    }

    paint();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Late layout (webfont, cutout decode) shifts the step centres; repaint
    // once everything has settled. Same paint, no new behaviour.
    window.addEventListener('load', onScroll);

    root.__pulseTechPinDestroy = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('load', onScroll);
      if (!window.ResizeObserver) window.removeEventListener('resize', setHead);
      if (ro) ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      root.__pulseTechPin = false;
    };
  }

  function initAll(scope) {
    var el = scope || document;
    if (el.matches && el.matches('[data-pulse-techpin]')) init(el);
    toArray(el.querySelectorAll('[data-pulse-techpin]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: sections are torn down and re-inserted.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    toArray(e.target.querySelectorAll('[data-pulse-techpin]')).forEach(function (el) {
      if (el.__pulseTechPinDestroy) el.__pulseTechPinDestroy();
    });
  });
})();

/* ---------------------------------------------------------------------------
   BOX RATIO FOLLOWS THE IMAGE (fix 2026-08-31).

   `.techpin__shoe` declared `aspect-ratio: 1600 / 1083`, a hardcoded guess in
   the section. The shots actually uploaded are SQUARE 1:1 cutouts, so a 1:1
   image inside a 1.477 box fitted by height and rendered 556px wide in an 822px
   box -- it looked shrunken, and because `.techpin__ring` is positioned in % OF
   THE BOX the pins sat off the shoe by the width of the dead space (266px
   measured at 1600).

   It cannot be derived in Liquid: these blocks fall back to a packaged theme
   asset, and `asset_url` exposes no dimensions. So it is taken from the decoded
   image at runtime, which is correct whatever the source and self-corrects if
   the artwork is ever re-cut to another ratio.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';
  function syncRatio(box) {
    var img = box.querySelector('.techpin__img');
    if (!img) return;
    function apply() {
      if (!img.naturalWidth || !img.naturalHeight) return;
      box.style.aspectRatio = img.naturalWidth + ' / ' + img.naturalHeight;
    }
    if (img.complete) apply();
    else img.addEventListener('load', apply, { once: true });
  }
  function init() {
    document.querySelectorAll('.techpin__shoe').forEach(syncRatio);
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
